import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  Share2, 
  Package, 
  Gift, 
  Star,
  Calendar,
  Tag,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Zap
} from "lucide-react";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-24">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#D4AF37]"></div>
  </div>
);

// Error Component
const ErrorMessage = ({ message, onRetry }) => (
  <div className="text-center py-24">
    <div className="text-red-500 mb-4 text-lg">
      Error: {message}
    </div>
    <button
      onClick={onRetry}
      className="bg-[#D4AF37] text-white px-6 py-3 rounded-sm hover:bg-[#C19B2E] transition-colors"
    >
      Try Again
    </button>
  </div>
);

// Image Gallery Component
const ImageGallery = ({ images, title }) => {
  const [currentImage, setCurrentImage] = useState(0);
  
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-200 rounded-sm flex items-center justify-center">
        <Package className="h-16 w-16 text-gray-400" />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="relative group">
        <img
          src={images[currentImage]}
          alt={`${title} - Image ${currentImage + 1}`}
          className="w-full h-full object-cover rounded-sm shadow-lg"
        />
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300"
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentImage + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              className={`flex-shrink-0 w-20 h-20 rounded-sm overflow-hidden border-2 transition-all duration-200 ${
                currentImage === index 
                  ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/50' 
                  : 'border-gray-200 hover:border-[#D4AF37]'
              }`}
            >
              <img
                src={image}
                alt={`${title} - Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ComboAndGiftsInfo = () => {
  const { id } = useParams();
  const location = useLocation();
  const [combo, setCombo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if combo data was passed via state
  const passedComboData = location.state?.comboData;

  const fetchComboDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // If combo data was passed via state, use it directly
      if (passedComboData && passedComboData._id === id) {
        setCombo(passedComboData);
        setIsLoading(false);
        return;
      }
      
      // Otherwise, fetch from API
      const response = await fetch(`${API_BASE_URL}/api/combos?id=${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Find the specific combo from the results array
      const foundCombo = data.results?.find(combo => combo._id === id);
      
      if (!foundCombo) {
        throw new Error('Combo pack not found');
      }
      
      setCombo(foundCombo);
    } catch (error) {
      console.error("Failed to fetch combo details:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchComboDetails();
    }
  }, [id, passedComboData]);

  const handleWhatsAppOrder = () => {
    if (!combo) return;

    const phoneNumber = "+917025959481";
    const message = `Hello! I'm interested in purchasing this combo pack:

🎁 *${combo.title}*
💰 Price: ₹${combo.price?.toLocaleString() || 'Contact for price'}
🆔 Product ID: ${combo.id}

${combo.description ? `📝 Description: ${combo.description}` : ''}

Please provide more details about availability and delivery.

Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: combo.title,
          text: combo.description,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
        fallbackShare();
      }
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    navigator.clipboard.writeText(window.location.href);
    // You could add a toast notification here
    alert('Link copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={fetchComboDetails} />;
  if (!combo) return <ErrorMessage message="Combo pack not found" onRetry={() => window.history.back()} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <Link
            to="/combo-gifts"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C19B2E] transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Combo Packs</span>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column - Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ImageGallery images={combo.images} title={combo.title} />
          </motion.div>

          {/* Right Column - Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              {combo.is_featured && (
                <div className="inline-flex items-center gap-1 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-white text-sm font-medium px-3 py-1 rounded-full mb-3">
                  <Star className="h-4 w-4" />
                  Featured Combo
                </div>
              )}
              
              <h1 
                className="text-3xl md:text-4xl text-[#333333] mb-3 tracking-wide"
                style={{ fontFamily: "serif" }}
              >
                {combo.title}
              </h1>
              
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <Tag className="h-4 w-4" />
                  <span>ID: {combo.id}</span>
                </div>
                {combo.created_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>Added: {formatDate(combo.created_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Price */}
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#C19B2E]/10 p-6 rounded-sm border border-[#D4AF37]/20">
              <div className="text-3xl font-bold text-[#333333] mb-2">
                {combo.price ? `₹${combo.price.toLocaleString()}` : 'Price on Request'}
              </div>
              <p className="text-sm text-gray-600">
                Special combo pricing - Save more when you buy together!
              </p>
            </div>

            {/* Description */}
            {combo.description && (
              <div>
                <h3 className="text-lg font-semibold text-[#333333] mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#D4AF37]" />
                  What's Included
                </h3>
                <p className="text-gray-700 leading-relaxed bg-white p-4 rounded-sm border border-gray-100">
                  {combo.description}
                </p>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${combo.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium text-gray-700">
                {combo.status === 'active' ? 'Available Now' : 'Currently Unavailable'}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* Primary Action - WhatsApp Order */}
              <button
                onClick={handleWhatsAppOrder}
                disabled={combo.status !== 'active'}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-white py-4 px-6 rounded-sm text-lg font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
              >
                <MessageCircle className="h-6 w-6" />
                <span>Order via WhatsApp</span>
                <Zap className="h-5 w-5" />
              </button>
              
              {combo.status !== 'active' && (
                <p className="text-sm text-red-500 text-center">
                  This combo is currently unavailable. Contact us for more information.
                </p>
              )}

              {/* Secondary Actions */}
              <div className="flex gap-3">
                
                
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-sm border border-gray-300 bg-white text-gray-700 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-200"
                >
                  <Share2 className="h-5 w-5" />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {/* Gift Notice */}
            <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#C19B2E]/10 p-4 rounded-sm border border-[#D4AF37]/20">
              <div className="flex items-center gap-3">
                <Gift className="h-6 w-6 text-[#D4AF37] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#333333] mb-1">Perfect for Gifting</h4>
                  <p className="text-sm text-gray-600">
                    This combo pack comes with elegant packaging, making it an ideal gift for special occasions.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-white p-6 md:p-8 rounded-sm shadow-lg"
        >
          <h3 className="text-2xl font-semibold text-[#333333] mb-6 text-center">
            Why Choose MASHO Combo Packs?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#333333] mb-2">Value for Money</h4>
              <p className="text-sm text-gray-600">
                Get more for less with our carefully curated combo packs
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#333333] mb-2">Premium Packaging</h4>
              <p className="text-sm text-gray-600">
                Elegant presentation perfect for gifting to loved ones
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#333333] mb-2">Handpicked Selection</h4>
              <p className="text-sm text-gray-600">
                Expertly chosen fragrances that complement each other
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ComboAndGiftsInfo;